import Lenis from '@studio-freight/lenis';
// import { each } from 'lodash';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade';
import { store as NavStore } from 'components/ui/Header/store';
import { defineStore } from 'pinia';
import { pinia } from 'plugins/store/index';
import { ScrollTrigger, gsap } from 'vendors/gsap';
import { computed, nextTick, ref } from 'vue';

interface ScrollState extends Obj {
  scrollable: boolean,
  mainScroll?: Lenis,
  ready: boolean,
  // on top of the page -> y = 0
  isTop: boolean,
  // on bottom of the page -> y = document height
  isBottom: boolean,
  // 1 == down /// -1 == up
  direction?: 1 | -1,
  // scroll y position
  y: number,
}

const useStore = defineStore('scroll', () => {
  const state = ref<ScrollState>({
    scrollable: false,
    mainScroll: undefined,
    ready: false,
    isTop: true,
    isBottom: false,
    direction: undefined,
    y: 0,
  });

  /** 
   * Create and register a new smooth scroll
   * @el HTMLElement who will be the scroll wrapper
   * @options ScrollOptions
  */
  const useSmoothScroll = (): void => {
    if (state.value.mainScroll) return;

    // window.scrollTo(0, 0);

    state.value.mainScroll = new Lenis({
      wrapper: document.body,
      // Lerp OR duration OR easing, duration will override lerp and if either of them are defined easing will be useless.
      lerp: 0.08,
      // duration: 3,
      // The easing function to use for the scroll animation, our default is custom but you can pick one from Easings.net. Useless if lerp defined
      // easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      syncTouch: true,
      syncTouchLerp: 0.1,
      touchInertiaMultiplier: 35,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
      autoResize: true,
      // Help safari with flickering issue
      wheelEventsTarget: document.body
    });

    addEvents();

    nextTick(() => {
      if (!state.value.scrollable) {
        state.value.mainScroll?.stop();
      }

      state.value.ready = true;
    });
  }

  const setReady = (): void => {
    setScrollable(true);
    state.value.ready = true;
  }

  const setScrollable = (scrollable: boolean): void => {
    // state.value.mainScroll?.paused(!scrollable);
    if (scrollable) {
      if (state.value.mainScroll) {
        state.value.mainScroll?.start()
      } else {
        enableBodyScroll(document.body)
      }
    } else {
      if (state.value.mainScroll) {
        state.value.mainScroll?.stop()
      } else {
        disableBodyScroll(document.body, {
          reserveScrollBarGap: true,
          //@ts-expect-error
          allowTouchMove: el => {
            while (el && el !== document.body) {
              //@ts-expect-error
              if (el.getAttribute('data-lenis-prevent') !== null) {
                return true;
              }
              //@ts-expect-error
              el = el.parentElement;
            }
          },
        })
      }
    }
    state.value.scrollable = scrollable;
    document.documentElement.dataset.scrollable = scrollable.toString()
  }

  /**
   * Add events for both ScrollTrigger and LocomotiveScroll
   */
  const addEvents = (): void => {
    if (state.value.mainScroll) {
      state.value.mainScroll.on('scroll', (scroll: any) => {
        state.value.isTop = scroll.targetScroll === 0;
        state.value.isBottom = scroll.targetScroll === scroll.limit;
        state.value.direction = scroll.direction;
        state.value.y = scroll.targetScroll;
      });

      gsap.ticker.add((time) => {
        state.value.mainScroll?.raf(time * 1000);
      })

      gsap.ticker.lagSmoothing(0);
    } else {
      document.addEventListener('scroll', () => {
        state.value.isTop = window.scrollY === 0;
        state.value.isBottom = window.scrollY + window.innerHeight >= document.body.scrollHeight
        state.value.direction = state.value.y < window.scrollY ? 1 : -1;
        state.value.y = window.scrollY;
      });
    }
  }

  /**
   * Remove events for both ScrollTrigger and LenisScroll
   */
  const removeEvents = (): void => {
    if (!state.value.mainScroll) return;
  }

  /**
   * Destroy and Reinit the scroll to refresh with the new size of the wrapper
   */
  const refreshScroll = (): void => {
    if (!state.value.mainScroll) return;
    ScrollTrigger.refresh();

    nextTick(() => {
      if (!state.value.scrollable) {
        state.value.mainScroll?.stop();
      }
    });
  }

  /**
   * Clean and destroy the smooth scroll instancr
   */
  const releaseSmoothScroll = (andClean = false): void => {
    if (!state.value.mainScroll) return;

    removeEvents();
    state.value.mainScroll.destroy();

    if (andClean) {
      state.value.mainScroll = undefined;
    }
  }

  const updateScroll = (): void => {
    if (!state.value.mainScroll) return;
    ScrollTrigger.refresh();
  }

  const scrollTo = (value: number | string | HTMLElement, options?: Obj): void => {
    const duration = options?.duration === undefined ? 2 : options?.duration;

    if (!state.value.mainScroll) {
      gsap.to(window, { duration: duration, scrollTo: { y: value, offsetY: (NavStore.height + 50), autoKill: false } });
    } else {
      state.value.mainScroll?.scrollTo(value, { duration: duration, immediate: duration === 0, offset: -(NavStore.height + 50), force: true, lock: true });
    }
  }

  const scrollTop = (): void => {
    if (!state.value.mainScroll) {
      gsap.to(window, { duration: 0, scrollTo: { y: 0, autoKill: false } });
      // patch for iphone :boom:
      setTimeout(() => {
        gsap.to(window, { duration: 0, scrollTo: { y: 0, autoKill: false } });
      }, 300);
      // window.scrollTo(0, 0);
    } else {
      state.value.mainScroll?.scrollTo(0, { duration: 0, force: true, immediate: true, lock: true });
    }
  }

  const getScroll = (): number => {
    return window.scrollY || 0;
  };

  const getDirection = (): number => {
    return state.value.direction || 0;
  };

  return {
    state,

    //getters
    ready: state.value.ready,
    scrollable: state.value.scrollable,
    scrollLimits: state.value.limits,
    mainScroll: computed(() => state.value.mainScroll),

    //actions
    useSmoothScroll,
    addEvents,
    setReady,
    releaseSmoothScroll,
    setScrollable,
    refreshScroll,
    updateScroll,
    scrollTo,
    scrollTop,
    getScroll,
    getDirection

  }
});

export const store = useStore(pinia);
export default store;
